import { Person } from "@/types";
import {
  getClassificationDisplay,
  getPrimaryFacilityName,
  getPrimarySectionName,
  getProgramName,
  getProgramStatus
} from "../_utils";
import { downloadCsv } from "./downloadCsv";
import type { PeopleQueryInput, PersonOrderByInput } from "../queries";

export const EXPORT_PAGE_SIZE = 100;
export const EXPORT_MAX_PAGES = 50;

type PageInfo = {
  hasNextPage?: boolean | null;
  endCursor?: string | null;
};

type PeopleConnection = {
  nodes?: Person[] | null;
  pageInfo?: PageInfo | null;
};

export type FetchDirectoryPeoplePage = (options: {
  variables: {
    input?: PeopleQueryInput;
    orderBy?: PersonOrderByInput;
    first: number;
    after?: string;
  };
}) => Promise<{ data?: { people?: PeopleConnection | null } | null; error?: Error }>;

export type DirectoryExportColumnOptions = {
  includeMembership: boolean;
  includeCareer: boolean;
  includeSection: boolean;
};

export function csvFilenameForDirectoryExport({
  programTypes,
  lockedSection,
  date = new Date()
}: {
  programTypes?: string[];
  lockedSection?: boolean;
  date?: Date;
}): string {
  const stamp = date.toISOString().slice(0, 10);
  const prefix = lockedSection ? "section-people" : "people";
  const programSlug = programTypes?.length === 1 ? programTypes[0].toLowerCase().replace(/_/g, "-") : undefined;
  return programSlug ? `${prefix}-${programSlug}-${stamp}.csv` : `${prefix}-${stamp}.csv`;
}

export function mergeDirectoryPeopleFields({
  people,
  membershipNodes,
  careerNodes
}: {
  people: Person[];
  membershipNodes?: Person[] | null;
  careerNodes?: Person[] | null;
}): Person[] {
  const membershipByPgaId = new Map<string, Person>();
  membershipNodes?.forEach((person) => {
    if (person.pgaId) {
      membershipByPgaId.set(person.pgaId, person);
    }
  });

  const careerByKey = new Map<string, Person>();
  careerNodes?.forEach((person) => {
    const key = person.pgaId ?? person.id;
    if (key) {
      careerByKey.set(key, person);
    }
  });

  return people.map((person) => {
    const membershipFields = person.pgaId ? membershipByPgaId.get(person.pgaId) : undefined;
    const key = person.pgaId ?? person.id;
    const careerFields = key ? careerByKey.get(key) : undefined;
    if (!membershipFields && !careerFields) return person;
    return {
      ...person,
      ...(membershipFields
        ? {
            latestProgram: membershipFields.latestProgram,
            ccmsSuspensions: membershipFields.ccmsSuspensions
          }
        : {}),
      ...(careerFields
        ? {
            primaryFacility: careerFields.primaryFacility,
            employments: careerFields.employments
          }
        : {})
    };
  });
}

export function personDisplayName(person: Person): string {
  if (person.displayName?.trim()) return person.displayName.trim();
  return [person.firstName, person.lastName].filter(Boolean).join(" ");
}

/**
 * Spreadsheet apps coerce unquoted numeric tokens to numbers and drop leading
 * zeros. Emit an Excel text formula so the displayed cell keeps the identifier.
 */
export function csvSpreadsheetTextCell(value: string): string {
  if (!value) return "";
  return `="${value}"`;
}

export function buildDirectoryPeopleCsvRows(
  people: Person[],
  { includeMembership, includeCareer, includeSection }: DirectoryExportColumnOptions
): string[][] {
  const headers = ["Name", "PGA ID"];
  if (includeMembership) {
    headers.push("Program", "Class", "Status");
  }
  if (includeSection) {
    headers.push("Section");
  }
  if (includeCareer) {
    headers.push("Primary Facility");
  }

  return [
    headers,
    ...people.map((person) => {
      const row = [personDisplayName(person), csvSpreadsheetTextCell(person.pgaId || "")];
      if (includeMembership) {
        row.push(getProgramName(person) || "", getClassificationDisplay(person) || "", getProgramStatus(person) || "");
      }
      if (includeSection) {
        row.push(getPrimarySectionName(person) || "");
      }
      if (includeCareer) {
        row.push(getPrimaryFacilityName(person) || "");
      }
      return row;
    })
  ];
}

type PageVariables = {
  input?: PeopleQueryInput;
  orderBy?: PersonOrderByInput;
  first: number;
  after?: string;
};

async function fetchConnectionPage(
  fetchPage: FetchDirectoryPeoplePage,
  variables: PageVariables
): Promise<PeopleConnection> {
  const { data, error } = await fetchPage({ variables });
  if (error) {
    throw error;
  }
  return data?.people ?? {};
}

async function fetchMembershipAndCareerPages({
  pageVariables,
  fetchMembershipPage,
  fetchCareerPage
}: {
  pageVariables: PageVariables;
  fetchMembershipPage?: FetchDirectoryPeoplePage;
  fetchCareerPage?: FetchDirectoryPeoplePage;
}): Promise<{ membershipNodes?: Person[] | null; careerNodes?: Person[] | null }> {
  const [membership, career] = await Promise.all([
    fetchMembershipPage ? fetchConnectionPage(fetchMembershipPage, pageVariables) : Promise.resolve(undefined),
    fetchCareerPage ? fetchConnectionPage(fetchCareerPage, pageVariables) : Promise.resolve(undefined)
  ]);
  return {
    membershipNodes: membership?.nodes,
    careerNodes: career?.nodes
  };
}

export const fetchAllDirectoryPeopleForExport = async ({
  fetchPeoplePage,
  fetchMembershipPage,
  fetchCareerPage,
  input,
  orderBy
}: {
  fetchPeoplePage: FetchDirectoryPeoplePage;
  fetchMembershipPage?: FetchDirectoryPeoplePage;
  fetchCareerPage?: FetchDirectoryPeoplePage;
  input?: PeopleQueryInput;
  orderBy?: PersonOrderByInput;
}): Promise<{ nodes: Person[]; truncated: boolean }> => {
  const allNodes: Person[] = [];
  let after: string | undefined = undefined;
  let hasNextPage = true;
  let pageCount = 0;

  while (hasNextPage && pageCount < EXPORT_MAX_PAGES) {
    const pageVariables = {
      input,
      orderBy,
      first: EXPORT_PAGE_SIZE,
      after
    };
    const connection = await fetchConnectionPage(fetchPeoplePage, pageVariables);
    const nodes = connection.nodes || [];
    const endCursor = connection.pageInfo?.endCursor;
    const nextPage = Boolean(connection.pageInfo?.hasNextPage);

    if (nodes.length === 0 || (nextPage && !endCursor)) {
      if (nodes.length > 0) {
        const { membershipNodes, careerNodes } = await fetchMembershipAndCareerPages({
          pageVariables,
          fetchMembershipPage,
          fetchCareerPage
        });
        allNodes.push(
          ...mergeDirectoryPeopleFields({
            people: nodes,
            membershipNodes,
            careerNodes
          })
        );
      }
      hasNextPage = false;
      break;
    }

    const { membershipNodes, careerNodes } = await fetchMembershipAndCareerPages({
      pageVariables,
      fetchMembershipPage,
      fetchCareerPage
    });

    allNodes.push(
      ...mergeDirectoryPeopleFields({
        people: nodes,
        membershipNodes,
        careerNodes
      })
    );
    hasNextPage = nextPage;
    after = endCursor ?? undefined;
    pageCount += 1;
  }

  return { nodes: allNodes, truncated: hasNextPage };
};

export const exportDirectoryPeopleCsv = ({
  people,
  columns,
  programTypes,
  lockedSection
}: {
  people: Person[];
  columns: DirectoryExportColumnOptions;
  programTypes?: string[];
  lockedSection?: boolean;
}): number => {
  downloadCsv(
    buildDirectoryPeopleCsvRows(people, columns),
    csvFilenameForDirectoryExport({ programTypes, lockedSection })
  );
  return people.length;
};
